import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Alert, Form, Spinner, Table, Pagination } from 'react-bootstrap';
import NavContainer from '../../components/Nav';
import OffCanvasNav from '../../components/OffCanvasNav';
import { useAuth } from '../../context/AuthContext';
import { dotoniwoofApi } from '../../api/dotoniwooApi';
import { Helmet } from 'react-helmet';
import Titulo from '../../elements/Titulo';
import IconSearch from '../../elements/IconSearch';

const GroomingDetail = () => {
  const { user } = useAuth();
  
  // Estados principales
  const [groomingSales, setGroomingSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  
  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const itemsPerPage = 10;
  
  // Estados para filtros
  const [filters, setFilters] = useState({
    date: '',
    year: '',
    month: '',
    day: ''
  });
  const [submittedFilters, setSubmittedFilters] = useState({
    date: '',
    year: '',
    month: '',
    day: ''
  });
  
  // Estado para controlar si se han aplicado filtros
  const [filtersApplied, setFiltersApplied] = useState(false);
  
  // Estado para mostrar alerta de "sin resultados"
  const [showNoResultsAlert, setShowNoResultsAlert] = useState(false);
  
  // Cargar datos de grooming
  const loadGroomingDetail = useCallback(async () => {
    const activeFilters = filtersApplied ? submittedFilters : null;
    const hasActiveFilters = !!(
      activeFilters?.date ||
      activeFilters?.year ||
      activeFilters?.month ||
      activeFilters?.day
    );

    try {
      setLoading(true);
      setErrorMsg('');
      
      // Construir parámetros de la URL
      const params = new URLSearchParams();
      params.append('page', currentPage);
      
      // Agregar filtros si están presentes
      if (activeFilters?.date) {
        // Asegurar que la fecha esté en formato YYYY-MM-DD
        const dateValue = activeFilters.date.includes('T') ? activeFilters.date.split('T')[0] : activeFilters.date;
        params.append('date', dateValue);
      } else {
        if (activeFilters?.year) {
          params.append('year', activeFilters.year);
        }
        if (activeFilters?.month) {
          params.append('month', activeFilters.month);
        }
        if (activeFilters?.day) {
          params.append('day', activeFilters.day);
        }
      }
      
      const queryString = params.toString();
      const url = `/sale.php/sale-gromming-detail${queryString ? `?${queryString}` : ''}`;

      const response = await dotoniwoofApi.get(url);
      
      if (response.data && response.data.status === 'ok') {
        const salesData = response.data.data || [];
        const totalRowsResponse = response.data.total_rows || 0;
        
        // Verificar si los datos devueltos coinciden con el filtro de fecha aplicado
        let dataMatchesFilter = true;
        if (filtersApplied && activeFilters?.date) {
          // Si hay filtro de fecha, verificar que todos los resultados coincidan
          const filterDate = new Date(activeFilters.date).toISOString().split('T')[0]; // YYYY-MM-DD
          dataMatchesFilter = salesData.some(sale => {
            if (sale.sale_date) {
              const saleDate = new Date(sale.sale_date).toISOString().split('T')[0];
              return saleDate === filterDate;
            }
            return false;
          });
          
          // Si no hay coincidencias, tratar como sin resultados
          if (!dataMatchesFilter && salesData.length > 0) {
            setGroomingSales([]);
            setTotalPages(1);
            setTotalRows(0);
            if (hasActiveFilters) {
              setShowNoResultsAlert(true);
            }
            return;
          }
        }
        
        setGroomingSales(salesData);
        setTotalPages(response.data.total_pages || 1);
        setTotalRows(totalRowsResponse);
        
        // Verificar si debe mostrarse la alerta después de actualizar estados
        if (filtersApplied && salesData.length === 0 && totalRowsResponse === 0) {
          if (hasActiveFilters) {
            setShowNoResultsAlert(true);
          } else {
            setShowNoResultsAlert(false);
          }
        } else {
          setShowNoResultsAlert(false);
        }
      } else {
        setErrorMsg(response.data?.message || 'Error al cargar datos');
        setGroomingSales([]);
        setTotalPages(1);
        setTotalRows(0);
        // Si hay filtros aplicados y hay error, también verificar alerta
        if (filtersApplied && hasActiveFilters) {
          setShowNoResultsAlert(true);
        }
      }
    } catch (error) {
      console.error('❌ Error loading grooming detail:', error);
      setErrorMsg(error.response?.data?.message || 'Error inesperado al cargar datos');
      setGroomingSales([]);
      setTotalPages(1);
      setTotalRows(0);
      // Si hay filtros aplicados y hay error, también verificar alerta
      if (filtersApplied && hasActiveFilters) {
        setShowNoResultsAlert(true);
      }
    } finally {
      setLoading(false);
    }
  }, [currentPage, filtersApplied, submittedFilters]);
  
  // Función auxiliar para formatear fecha en el alert
  const formatDateForAlert = (dateString) => {
    if (!dateString) return '';
    try {
      // Si la fecha viene en formato YYYY-MM-DD (del input type="date")
      // o si viene con hora, extraer solo la fecha
      let dateToFormat = dateString;
      if (dateString.includes('T')) {
        dateToFormat = dateString.split('T')[0];
      }
      
      // Crear la fecha correctamente (el input type="date" devuelve YYYY-MM-DD)
      const [year, month, day] = dateToFormat.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      
      return date.toLocaleDateString('es-CO', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formateando fecha:', error, dateString);
      return dateString;
    }
  };
  
  // Cargar datos al montar, al paginar o al aplicar/limpiar filtros
  useEffect(() => {
    loadGroomingDetail();
  }, [loadGroomingDetail]);
  
  // Verificar si debe mostrarse la alerta después de cargar datos (respaldo)
  useEffect(() => {
    if (filtersApplied && !loading && groomingSales.length === 0 && totalRows === 0) {
      const hasFilters = submittedFilters.date || submittedFilters.year || submittedFilters.month || submittedFilters.day;
      if (hasFilters) {
        setShowNoResultsAlert(true);
      } else {
        setShowNoResultsAlert(false);
      }
    } else if (groomingSales.length > 0 || totalRows > 0) {
      // Si hay datos, ocultar la alerta
      setShowNoResultsAlert(false);
    }
  }, [groomingSales, totalRows, filtersApplied, submittedFilters, loading]);
  
  // Manejar cambio de filtros
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    // Mantener los resultados actuales hasta que el usuario presione "Buscar".
  };
  
  // Manejar búsqueda
  const handleSearch = async () => {
    // Validar que haya al menos un filtro
    if (!filters.date && !filters.year && !filters.month && !filters.day) {
      setErrorMsg('Por favor, seleccione al menos un filtro para buscar');
      setGroomingSales([]);
      setTotalPages(1);
      setTotalRows(0);
      return;
    }
    
    setSubmittedFilters({ ...filters });
    setCurrentPage(1);
    setFiltersApplied(true);
    setErrorMsg('');
    setShowNoResultsAlert(false);
    // La carga se hará automáticamente por el useEffect
  };
  
  // Limpiar filtros
  const handleClearFilters = () => {
    setFilters({
      date: '',
      year: '',
      month: '',
      day: ''
    });
    setSubmittedFilters({
      date: '',
      year: '',
      month: '',
      day: ''
    });
    setCurrentPage(1);
    setFiltersApplied(false);
    setErrorMsg('');
    setShowNoResultsAlert(false);
  };
  
  // Manejar cambio de página
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  
  // Formatear fecha para mostrar
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('es-CO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };
  
  // Formatear precio
  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };
  
  return (
    <>
      <NavContainer />
      <OffCanvasNav />
      <Helmet>
        <title>Detalle de Ventas de Grooming - DotoniWoof</title>
      </Helmet>
      
      <Container fluid className="py-4">
        <Row>
          <Col>
            <Titulo>Detalle de Ventas de Grooming</Titulo>
            
            {/* Filtros */}
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">
                  <i className="fas fa-filter me-2"></i>
                  Filtros de Búsqueda
                </h5>
              </Card.Header>
              <Card.Body>
                <Form>
                  <Row>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Filtrar por Fecha Específica</Form.Label>
                        <Form.Control
                          type="date"
                          value={filters.date}
                          onChange={(e) => handleFilterChange('date', e.target.value)}
                          placeholder="Seleccione una fecha"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>Año</Form.Label>
                        <Form.Control
                          type="number"
                          min="2020"
                          max={new Date().getFullYear() + 1}
                          value={filters.year}
                          onChange={(e) => handleFilterChange('year', e.target.value)}
                          placeholder="YYYY"
                          disabled={!!filters.date}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>Mes</Form.Label>
                        <Form.Control
                          type="number"
                          min="1"
                          max="12"
                          value={filters.month}
                          onChange={(e) => handleFilterChange('month', e.target.value)}
                          placeholder="MM"
                          disabled={!!filters.date}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>Día</Form.Label>
                        <Form.Control
                          type="number"
                          min="1"
                          max="31"
                          value={filters.day}
                          onChange={(e) => handleFilterChange('day', e.target.value)}
                          placeholder="DD"
                          disabled={!!filters.date}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3} className="d-flex align-items-end">
                      <div className="w-100">
                        <button
                          type="button"
                          className="btn btn-primary me-2"
                          onClick={handleSearch}
                          disabled={loading}
                        >
                          <IconSearch /> Buscar
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={handleClearFilters}
                          disabled={loading}
                        >
                          Limpiar
                        </button>
                      </div>
                    </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>
            
            {/* Mensajes de error */}
            {errorMsg && (
              <Alert variant="danger" dismissible onClose={() => setErrorMsg('')}>
                <Alert.Heading>Error</Alert.Heading>
                <p>{errorMsg}</p>
              </Alert>
            )}
            
            {/* Alerta de sin resultados */}
            {showNoResultsAlert && !loading && (
              <Alert variant="danger" dismissible onClose={() => setShowNoResultsAlert(false)} className="mb-3">
                <Alert.Heading>
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  No se encontraron resultados
                </Alert.Heading>
                <p className="mb-2">
                  No hay ventas de grooming registradas para los parámetros de búsqueda seleccionados.
                </p>
                <div className="mt-2">
                  <strong>Filtros aplicados:</strong>
                  <ul className="mb-0 mt-2">
                    {filters.date && (
                      <li><i className="fas fa-calendar me-2"></i>Fecha: {formatDateForAlert(filters.date)}</li>
                    )}
                    {filters.year && (
                      <li><i className="fas fa-calendar-alt me-2"></i>Año: {filters.year}</li>
                    )}
                    {filters.month && (
                      <li><i className="fas fa-calendar me-2"></i>Mes: {filters.month}</li>
                    )}
                    {filters.day && (
                      <li><i className="fas fa-calendar-day me-2"></i>Día: {filters.day}</li>
                    )}
                  </ul>
                </div>
              </Alert>
            )}
            
            {/* Información de resultados */}
            {!loading && totalRows > 0 && (
              <Alert variant="info" className="mb-3">
                Mostrando {groomingSales.length} de {totalRows} resultados
                {totalPages > 1 && ` - Página ${currentPage} de ${totalPages}`}
              </Alert>
            )}
            
            {/* Tabla de resultados */}
            <Card>
              <Card.Header>
                <h5 className="mb-0">
                  <i className="fas fa-cut me-2"></i>
                  Ventas de Grooming
                </h5>
              </Card.Header>
              <Card.Body>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3">Cargando datos...</p>
                  </div>
                ) : groomingSales.length === 0 && !filtersApplied ? (
                  <Alert variant="info" className="text-center">
                    <i className="fas fa-info-circle me-2"></i>
                    No hay datos disponibles. Use los filtros para buscar ventas de grooming.
                  </Alert>
                ) : (
                  <>
                    <div className="table-responsive">
                      <Table striped bordered hover responsive>
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Fecha y Hora</th>
                            <th>Descripción</th>
                            <th className="text-end">Precio Unitario</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groomingSales.map((sale, index) => (
                            <tr key={index}>
                              <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                              <td>{formatDate(sale.sale_date)}</td>
                              <td>{sale.description || '-'}</td>
                              <td className="text-end">{formatPrice(sale.unit_price)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                    
                    {/* Paginación */}
                    {totalPages > 1 && (
                      <div className="d-flex justify-content-center mt-4">
                        <Pagination>
                          <Pagination.First
                            onClick={() => handlePageChange(1)}
                            disabled={currentPage === 1}
                          />
                          <Pagination.Prev
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                          />
                          
                          {Array.from({ length: totalPages }, (_, i) => i + 1)
                            .filter(page => {
                              // Mostrar primera, última, actual y adyacentes
                              return (
                                page === 1 ||
                                page === totalPages ||
                                (page >= currentPage - 2 && page <= currentPage + 2)
                              );
                            })
                            .map((page, index, array) => {
                              // Agregar elipsis si hay gap
                              const showEllipsis = index > 0 && array[index] - array[index - 1] > 1;
                              return (
                                <React.Fragment key={page}>
                                  {showEllipsis && <Pagination.Ellipsis />}
                                  <Pagination.Item
                                    active={page === currentPage}
                                    onClick={() => handlePageChange(page)}
                                  >
                                    {page}
                                  </Pagination.Item>
                                </React.Fragment>
                              );
                            })}
                          
                          <Pagination.Next
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          />
                          <Pagination.Last
                            onClick={() => handlePageChange(totalPages)}
                            disabled={currentPage === totalPages}
                          />
                        </Pagination>
                      </div>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default GroomingDetail;

